import React, { useEffect, useMemo, useState } from 'react';
import { resolveAnyStaticUrl } from '../../urlConfig';
import {
    getPricingProductDetails,
    listManualReviewCandidates,
} from './catalogReviewApi';

const formatPrice = (value) => Number(value || 0) > 0
    ? `${Number(value).toLocaleString('ru-RU')} ₽` : '—';

const toImages = (product = {}) => Array.from(new Set([
    product.reducedImage,
    ...(Array.isArray(product.galleryImages) ? product.galleryImages : []),
    ...(Array.isArray(product.productPictures) ? product.productPictures
        .filter((image) => image?.img && image.visibleImg !== false).map((image) => image.img) : []),
].filter(Boolean)));

const sizeText = (product = {}) => {
    const dimensions = product.pricingModelMeasurement?.dimensions || {};
    const fallback = product.productPricing?.baseDimensions || {};
    const source = Number(dimensions.height) > 0 ? dimensions : fallback;
    return [source.height, source.width, source.thickness].every((v) => Number(v) > 0)
        ? `${source.height} × ${source.width} × ${source.thickness} мм` : 'Не указан';
};

const productName = (product) => product?.fullName || product?.name || `Товар ${product?.article || ''}`;

/**
 * Самостоятельный компонент сравнения существующих Product._id.
 * Подборку можно пополнять прямо из модального окна, включая товары без 3D.
 * Никаких изменений товаров/цен/связей здесь не производится.
 */
const AdminProductCompareModal = ({
    productIds = [], onClose, onRemove, onAdd, maxItems = 4,
}) => {
    const ids = useMemo(() => Array.from(new Set(productIds.map(String).filter(Boolean)))
        .slice(0, maxItems), [productIds, maxItems]);
    const selectedIdsKey = ids.join('|');
    const [products, setProducts] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [selectedImages, setSelectedImages] = useState({});
    const [expanded, setExpanded] = useState({});
    const [pickerOpen, setPickerOpen] = useState(true);
    const [searchDraft, setSearchDraft] = useState('');
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [candidates, setCandidates] = useState([]);
    const [candidatePagination, setCandidatePagination] = useState(null);
    const [candidateBusy, setCandidateBusy] = useState(false);
    const [candidateError, setCandidateError] = useState('');

    useEffect(() => {
        let active = true;
        if (!ids.length) {
            setProducts({});
            setBusy(false);
            return undefined;
        }
        setBusy(true);
        setError('');
        Promise.all(ids.map(async (id) => {
            try {
                const result = await getPricingProductDetails(id);
                return [id, result?.product || null];
            } catch (_error) {
                return [id, null];
            }
        })).then((pairs) => {
            if (!active) return;
            setProducts(Object.fromEntries(pairs));
            if (pairs.some((pair) => !pair[1])) setError('Некоторые товары не удалось загрузить.');
        }).finally(() => { if (active) setBusy(false); });
        return () => { active = false; };
    // selectedIdsKey deliberately represents the stable contents of the selected set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIdsKey]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            setSearch(searchDraft.trim());
            setPage(0);
        }, 280);
        return () => window.clearTimeout(timer);
    }, [searchDraft]);

    useEffect(() => {
        if (!pickerOpen || ids.length >= maxItems) return undefined;
        let active = true;
        setCandidateBusy(true);
        setCandidateError('');
        listManualReviewCandidates({
            search, page, limit: 12, includeWithout3D: true,
        }).then((result) => {
            if (!active) return;
            setCandidates(Array.isArray(result?.rows) ? result.rows : []);
            setCandidatePagination(result?.pagination || null);
        }).catch((requestError) => {
            if (!active) return;
            setCandidateError(requestError?.response?.data?.message || 'Не удалось загрузить товары.');
            setCandidates([]);
        }).finally(() => { if (active) setCandidateBusy(false); });
        return () => { active = false; };
    }, [ids.length, maxItems, page, pickerOpen, search]);

    useEffect(() => {
        const previous = document.body.style.overflow;
        const keyHandler = (event) => { if (event.key === 'Escape') onClose?.(); };
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', keyHandler);
        return () => {
            document.body.style.overflow = previous;
            document.removeEventListener('keydown', keyHandler);
        };
    }, [onClose]);

    const chooseProduct = (id) => {
        const normalized = String(id || '');
        if (!normalized || ids.includes(normalized) || ids.length >= maxItems) return;
        onAdd?.(normalized);
    };

    return (
        <div className="catalogProductCompare__backdrop" role="presentation" onMouseDown={onClose}>
            <section className="catalogProductCompare" role="dialog" aria-modal="true"
                aria-label="Сравнение товаров" onMouseDown={(event) => event.stopPropagation()}>
                <header className="catalogProductCompare__header">
                    <div>
                        <strong>Сравнение товаров</strong>
                        <small>Выбрано {ids.length} из {maxItems} · сравнение фотографий, размеров, цен и 3D</small>
                    </div>
                    <div className="catalogProductCompare__headerActions">
                        <button type="button" className="catalogProductCompare__addToggle"
                            onClick={() => setPickerOpen((open) => !open)}
                            disabled={ids.length >= maxItems && !pickerOpen}>
                            {pickerOpen ? 'Скрыть поиск' : '+ Добавить товар'}
                        </button>
                        <button type="button" className="catalogProductCompare__close"
                            onClick={onClose} aria-label="Закрыть">×</button>
                    </div>
                </header>

                {pickerOpen ? (
                    <div className="catalogProductCompare__picker">
                        <div className="catalogProductCompare__pickerTop">
                            <label htmlFor="catalog-compare-search">Добавить товар к сравнению</label>
                            <span>Можно искать по названию или артикулу в любом каталоге, даже без 3D.</span>
                        </div>
                        <input id="catalog-compare-search" type="search" value={searchDraft}
                            onChange={(event) => setSearchDraft(event.target.value)}
                            placeholder="Например: ПР 0012, Волна, 100525…"
                            disabled={ids.length >= maxItems} />
                        {ids.length >= maxItems ? (
                            <p className="catalogProductCompare__hint">Достигнут лимит: убери один товар, чтобы добавить другой.</p>
                        ) : (
                            <>
                                {candidateBusy ? <p className="catalogProductCompare__hint">Ищем товары…</p> : null}
                                {candidateError ? <p className="catalogProductCompare__error">{candidateError}</p> : null}
                                {!candidateBusy && !candidateError && candidates.length === 0 ? (
                                    <p className="catalogProductCompare__hint">Подходящих товаров не найдено.</p>
                                ) : null}
                                <div className="catalogProductCompare__results">
                                    {candidates.map((candidate) => {
                                        const id = String(candidate._id || '');
                                        const selected = ids.includes(id);
                                        return (
                                            <div key={id} className={`catalogProductCompare__result ${selected ? 'is-selected' : ''}`}>
                                                {candidate.reducedImage ? (
                                                    <img src={resolveAnyStaticUrl(candidate.reducedImage)}
                                                        alt={productName(candidate)} loading="lazy" />
                                                ) : <span className="catalogProductCompare__noPhoto">Нет фото</span>}
                                                <div className="catalogProductCompare__resultText">
                                                    <b title={productName(candidate)}>{productName(candidate)}</b>
                                                    <small>Арт. {candidate.article || '—'} · {candidate.modelPath ? '3D есть' : 'Без 3D'}</small>
                                                </div>
                                                <button type="button" disabled={selected || ids.length >= maxItems || !onAdd}
                                                    onClick={() => chooseProduct(id)}>
                                                    {selected ? 'Добавлен' : '+ Добавить'}
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                                {(candidatePagination?.totalPages || 1) > 1 ? (
                                    <div className="catalogProductCompare__paging">
                                        <button type="button" disabled={page <= 0 || candidateBusy}
                                            onClick={() => setPage((value) => Math.max(0, value - 1))}>← Назад</button>
                                        <span>{page + 1} / {candidatePagination.totalPages}</span>
                                        <button type="button" disabled={page + 1 >= candidatePagination.totalPages || candidateBusy}
                                            onClick={() => setPage((value) => value + 1)}>Вперёд →</button>
                                    </div>
                                ) : null}
                            </>
                        )}
                    </div>
                ) : null}

                {busy ? <p className="catalogProductCompare__hint">Обновляем карточки…</p> : null}
                {error ? <p className="catalogProductCompare__error">{error}</p> : null}

                <div className="catalogProductCompare__scroll">
                    {!ids.length ? (
                        <div className="catalogProductCompare__empty">
                            <b>Пока нет товаров для сравнения</b>
                            <span>Найди товар в строке поиска и нажми «Добавить».</span>
                        </div>
                    ) : (
                        <div className="catalogProductCompare__grid" style={{ '--compare-count': ids.length }}>
                            {ids.map((id) => {
                                const product = products[id];
                                if (!product) {
                                    return <article key={id} className="catalogProductCompare__item">
                                        <p className="catalogProductCompare__hint">Загружаем данные товара…</p>
                                        {onRemove ? <button type="button" onClick={() => onRemove(id)}>Убрать</button> : null}
                                    </article>;
                                }
                                const photos = toImages(product);
                                const imageIndex = Math.min(Number(selectedImages[id] || 0), Math.max(photos.length - 1, 0));
                                return (
                                    <article key={id} className="catalogProductCompare__item">
                                        <div className="catalogProductCompare__itemHeader">
                                            <strong>{productName(product)}</strong>
                                            {onRemove ? <button type="button" onClick={() => onRemove(id)}
                                                title="Убрать из сравнения" aria-label={`Убрать ${productName(product)}`}>×</button> : null}
                                        </div>
                                        <div className="catalogProductCompare__mainPhoto">
                                            {photos[imageIndex] ? <img src={resolveAnyStaticUrl(photos[imageIndex])}
                                                alt={productName(product)} /> : <span>Нет фотографий</span>}
                                            <div className="catalogProductCompare__photoNavigation">
                                                <button type="button" disabled={photos.length < 2} onClick={() => setSelectedImages((old) => ({
                                                    ...old, [id]: (imageIndex - 1 + photos.length) % photos.length,
                                                }))}>‹</button>
                                                <small>{photos.length ? `${imageIndex + 1} / ${photos.length}` : '0 фото'}</small>
                                                <button type="button" disabled={photos.length < 2} onClick={() => setSelectedImages((old) => ({
                                                    ...old, [id]: (imageIndex + 1) % photos.length,
                                                }))}>›</button>
                                            </div>
                                        </div>
                                        <div className="catalogProductCompare__thumbs">
                                            {photos.map((src, index) => (
                                                <button key={`${src}-${index}`} type="button"
                                                    className={index === imageIndex ? 'is-active' : ''}
                                                    title={`Фотография ${index + 1}`}
                                                    onClick={() => setSelectedImages((old) => ({ ...old, [id]: index }))}>
                                                    <img src={resolveAnyStaticUrl(src)} alt={`Миниатюра ${index + 1}`} loading="lazy" />
                                                </button>
                                            ))}
                                        </div>
                                        <dl className="catalogProductCompare__properties">
                                            <div><dt>Артикул</dt><dd>{product.article || '—'}</dd></div>
                                            <div><dt>Категория</dt><dd>{product.categoryName || '—'}</dd></div>
                                            <div><dt>Номер купленной формы</dt><dd>{product.catalogModelNumber || 'Не назначен'}</dd></div>
                                            <div><dt>3D</dt><dd className={product.modelPath ? 'is-ok' : 'is-missing'}>
                                                {product.modelPath ? 'Есть модель' : 'Нет модели'}</dd></div>
                                            <div><dt>Размер</dt><dd>{sizeText(product)}</dd></div>
                                            <div><dt>Цена фрезеровки</dt><dd>{formatPrice(product.millingPricing?.basePrice || product.workPrice)}</dd></div>
                                            <div><dt>Цена товара</dt><dd>{formatPrice(product.price)}</dd></div>
                                        </dl>
                                        <button type="button" className="catalogProductCompare__detailsButton"
                                            onClick={() => setExpanded((old) => ({ ...old, [id]: !old[id] }))}>
                                            {expanded[id] ? 'Скрыть подробности' : 'Показать подробности'}
                                        </button>
                                        {expanded[id] ? <div className="catalogProductCompare__details">
                                            <b>Описание</b><p>{product.descriptionNew || product.description || 'Не заполнено'}</p>
                                            <b>Теги</b><p>{(product.hashtags || []).join(', ') || '—'}</p>
                                            <b>Исходные размеры</b><p>{[product.height, product.width, product.length].filter(Boolean).join(' × ') || '—'}</p>
                                            <b>3D-файл</b><p>{product.modelPath || 'Не загружен'}</p>
                                        </div> : null}
                                    </article>
                                );
                            })}
                        </div>
                    )}
                </div>
                <footer>
                    <span>{ids.length} из {maxItems} товаров</span>
                    <button type="button" onClick={onClose}>Закрыть сравнение</button>
                </footer>
            </section>
        </div>
    );
};

export default AdminProductCompareModal;
